import { z } from 'zod'

import { ERROR_CATALOG, ERROR_CODES } from './errors'
import { ROUTES, type RouteDefinition } from './registry'

/**
 * The OpenAPI 3.1 document, generated from the zod schemas themselves.
 *
 * Generated rather than written, because a hand-maintained API document is a
 * second source of truth and the second one is always the one that is wrong.
 * `z.toJSONSchema` (zod 4) emits draft-2020-12, which OpenAPI 3.1 uses as its
 * schema dialect directly — so no adapter library is needed and none is added.
 *
 * Responses are wrapped in the envelope of §5.1 (`{ data, meta? }`) here rather
 * than in each schema, so the wrapper exists in exactly one place and a route
 * cannot forget it.
 */

type JsonSchema = Record<string, unknown>

const toJsonSchema = (schema: z.ZodType, io: 'input' | 'output'): JsonSchema => {
  const json = z.toJSONSchema(schema, {
    target: 'draft-2020-12',
    io,
    // Nothing in this package is unrepresentable today; `any` keeps a future
    // addition from turning the whole document into a build failure.
    unrepresentable: 'any',
  }) as JsonSchema
  // The dialect is declared once on the document, not on every component.
  delete json.$schema
  return json
}

/** `/animals/:id/costs/:costId` → `/animals/{id}/costs/{costId}`. */
const toOpenApiPath = (path: string): string => path.replace(/:([A-Za-z0-9_]+)/g, '{$1}')

const pathParameters = (path: string) =>
  Array.from(path.matchAll(/:([A-Za-z0-9_]+)/g)).map(([, name]) => ({
    name,
    in: 'path' as const,
    required: true,
    schema: { type: 'string' },
    description: name === 'id' ? 'ULID.' : undefined,
  }))

/** A query schema becomes one parameter per top-level property. */
const queryParameters = (schema: z.ZodType) => {
  const json = toJsonSchema(schema, 'input')
  const properties = (json.properties ?? {}) as Record<string, JsonSchema>
  const required = new Set((json.required as string[] | undefined) ?? [])
  return Object.entries(properties).map(([name, property]) => ({
    name,
    in: 'query' as const,
    required: required.has(name),
    schema: property,
  }))
}

const ENVELOPE_META = {
  type: 'object',
  properties: {
    nextCursor: { type: ['string', 'null'], description: 'Null on the last page.' },
    total: { type: 'integer', minimum: 0 },
  },
} as const

const successEnvelope = (data: JsonSchema, paginated: boolean): JsonSchema => ({
  type: 'object',
  required: ['data'],
  properties: paginated ? { data, meta: ENVELOPE_META } : { data },
  additionalProperties: false,
})

const errorResponse = (description: string) => ({
  description,
  content: { 'application/json': { schema: { $ref: '#/components/schemas/ApiError' } } },
})

const securityFor = (auth: RouteDefinition['auth']) => {
  switch (auth) {
    case 'none':
      return []
    case 'signupToken':
      return [{ signupToken: [] }]
    default:
      return [{ bearerAuth: [] }, { cookieAuth: [] }]
  }
}

const operationFor = (route: RouteDefinition) => {
  const parameters = [
    ...pathParameters(route.path),
    ...(route.requestIn === 'query' && route.request ? queryParameters(route.request) : []),
  ]

  const responses: Record<string, unknown> = {}
  if (route.binary) {
    responses['200'] = {
      description: 'A stream of bytes.',
      content: { 'application/octet-stream': { schema: { type: 'string', format: 'binary' } } },
    }
  } else if (route.response) {
    responses[route.method === 'POST' ? '201' : '200'] = {
      description: route.responseName ?? 'OK',
      content: {
        'application/json': {
          schema: successEnvelope(toJsonSchema(route.response, 'output'), route.paginated === true),
        },
      },
    }
  } else {
    responses['204'] = { description: 'No content.' }
  }
  responses['4XX'] = errorResponse('One of the closed error codes in §5.2.')
  responses['500'] = errorResponse('INTERNAL. Never a stack trace, never SQL.')

  const requestBody =
    route.request && route.requestIn !== 'query'
      ? {
          required: true,
          content: {
            [route.requestIn === 'multipart' ? 'multipart/form-data' : 'application/json']: {
              schema: toJsonSchema(route.request, 'input'),
            },
          },
        }
      : undefined

  return {
    operationId: `${route.id}_${route.method.toLowerCase()}_${route.path
      .replace(/[^A-Za-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')}`,
    summary: route.summary,
    description: [`Plan §5 ${route.id}. Role: ${route.role}.`, route.notes]
      .filter(Boolean)
      .join(' '),
    tags: [route.id.startsWith('P') ? 'platform' : route.id.startsWith('O') ? 'owner' : 'trader'],
    security: securityFor(route.auth),
    ...(parameters.length > 0 ? { parameters } : {}),
    ...(requestBody ? { requestBody } : {}),
    responses,
  }
}

export const buildOpenApiDocument = (
  options: { title?: string; version?: string; serverUrl?: string } = {},
) => {
  const paths: Record<string, Record<string, unknown>> = {}
  for (const route of ROUTES) {
    const path = toOpenApiPath(route.path)
    paths[path] ??= {}
    paths[path][route.method.toLowerCase()] = operationFor(route)
  }

  return {
    openapi: '3.1.0',
    info: {
      title: options.title ?? 'Cattle Book API',
      version: options.version ?? '1.0.0',
      description:
        'Generated from packages/contracts. Money is integer paisa in keys suffixed `Paisa`, ' +
        'and a money key is ABSENT from a response when the actor may not see it (§4.16) — ' +
        'never null, never zero, never masked. Date-only fields are YYYY-MM-DD; instants are ' +
        'ISO-8601 with an offset.',
    },
    servers: [{ url: options.serverUrl ?? '/api/v1' }],
    security: [{ bearerAuth: [] }, { cookieAuth: [] }],
    components: {
      securitySchemes: {
        bearerAuth: { type: 'http', scheme: 'bearer', description: 'Mobile.' },
        cookieAuth: {
          type: 'apiKey',
          in: 'cookie',
          name: 'cb_session',
          description: 'Web. Mutations also require X-CSRF-Token.',
        },
        signupToken: { type: 'http', scheme: 'bearer', description: 'A3 only, 15 minutes.' },
      },
      schemas: {
        ApiError: {
          type: 'object',
          required: ['error'],
          properties: {
            error: {
              type: 'object',
              required: ['code', 'message', 'requestId'],
              properties: {
                code: { type: 'string', enum: [...ERROR_CODES] },
                message: { type: 'string' },
                details: { type: 'object', additionalProperties: true },
                requestId: { type: 'string' },
              },
            },
          },
        },
      },
    },
    'x-error-catalog': Object.fromEntries(
      Object.entries(ERROR_CATALOG).map(([code, definition]) => [code, definition.status]),
    ),
    paths,
  }
}

/** The document itself, for anyone who just wants to serve or diff it. */
export const openApiDocument = buildOpenApiDocument()
